import { PubSub } from "@aws-amplify/pubsub";

// Initialize PubSub client
const pubsub = new PubSub({
  region: "eu-west-1",
  endpoint: "wss://aa548ngrivwoo-ats.iot.eu-west-1.amazonaws.com/mqtt",
});

/**
 * Extract payload from IoT PubSub message.
 * Handles different message formats from IoT Core.
 */
function extractPayload<T>(msg: any): T | null {
  try {
    // Unwrap PubSub value wrapper
    let payload = msg?.value ?? msg;
    // Parse string payloads
    if (typeof payload === "string") {
      payload = JSON.parse(payload);
    }
    // For chunked messages, return the full chunk object for reassembly
    if (payload?.chunked) {
      return payload as T;
    }
    // For regular messages, extract the inner data (DynamoDB record)
    if (payload?.data) {
      return payload.data as T;
    }
    return payload as T;
  } catch (e) {
    console.error("Error extracting IoT payload:", e);
    return null;
  }
}

interface ChunkBuffer {
  chunks: Map<number, string>;
  totalChunks: number;
  receivedAt: number;
}

const CHUNK_BUFFER_TTL_MS = 30_000; // Discard incomplete buffers after 30s
const MAX_PENDING_MESSAGES = 100; // Cap on concurrent incomplete message buffers

/**
 * Manages reassembly of chunked IoT messages.
 * When a large payload exceeds the 128KB MQTT limit, the publisher splits it
 * into multiple messages with { chunked: true, messageId, chunkIndex, totalChunks, data }.
 * This class buffers chunks and returns the reassembled payload once all chunks arrive.
 */
class ChunkReassembler {
  private buffers = new Map<string, ChunkBuffer>();

  /**
   * Process an incoming message. Returns the reassembled payload if all chunks
   * have been received, or null if still waiting for more chunks.
   * For non-chunked messages, returns the message as-is.
   */
  process<T>(msg: any): T | null {
    if (!msg?.chunked) {
      return msg as T;
    }

    const { messageId, chunkIndex, totalChunks, data } = msg;

    if (!this.buffers.has(messageId)) {
      // Evict oldest entry if we've hit the pending-message cap
      if (this.buffers.size >= MAX_PENDING_MESSAGES) {
        const oldestKey = this.buffers.keys().next().value;
        if (oldestKey !== undefined) {
          console.warn(
            `ChunkReassembler: pending buffer limit (${MAX_PENDING_MESSAGES}) reached, evicting ${oldestKey}`
          );
          this.buffers.delete(oldestKey);
        }
      }
      this.buffers.set(messageId, {
        chunks: new Map(),
        totalChunks,
        receivedAt: Date.now(),
      });
      this.cleanupStaleBuffers();
    }

    const buffer = this.buffers.get(messageId)!;
    buffer.chunks.set(chunkIndex, data);

    if (buffer.chunks.size < totalChunks) {
      return null; // Still waiting for more chunks
    }

    // All chunks received — reassemble
    let assembled = "";
    for (let i = 0; i < totalChunks; i++) {
      assembled += buffer.chunks.get(i);
    }
    this.buffers.delete(messageId);

    try {
      const result = JSON.parse(assembled);
      // Extract inner data (DynamoDB record) from the Lambda payload wrapper
      return (result?.data ?? result) as T;
    } catch (e) {
      console.error("Failed to parse reassembled chunked message:", e);
      return null;
    }
  }

  private cleanupStaleBuffers(): void {
    const now = Date.now();
    this.buffers.forEach((buffer, id) => {
      if (now - buffer.receivedAt > CHUNK_BUFFER_TTL_MS) {
        console.warn(`Discarding stale chunk buffer: ${id}`);
        this.buffers.delete(id);
      }
    });
  }
}

export interface SubscriptionConfig<T> {
  /** The IoT topic to subscribe to */
  topic: string;
  /** Callback when a message is received */
  onMessage: (data: T) => void;
  /** Optional callback for errors */
  onError?: (error: any) => void;
  /** Maximum number of retry attempts (default: 5) */
  maxRetries?: number;
  /** Delay between retries in milliseconds (default: 2000) */
  retryDelay?: number;
  /** Optional callback when subscription is established */
  onSubscribed?: () => void;
}

export interface Subscription {
  /** Unsubscribe from the topic */
  unsubscribe: () => void;
  /** Get the subscription topic */
  getTopic: () => string;
  /** Check if subscription is active */
  isActive: () => boolean;
}

class SubscriptionManager {
  private subscriptions = new Map<string, { unsubscribe: () => void }>();
  private reassembler = new ChunkReassembler();

  /**
   * Subscribe to an IoT topic with automatic retry logic
   */
  subscribe<T>(config: SubscriptionConfig<T>): Subscription {
    const {
      topic,
      onMessage,
      onError,
      maxRetries = 5,
      retryDelay = 2000,
      onSubscribed,
    } = config;

    let subscription: { unsubscribe: () => void } | null = null;
    let retryCount = 0;
    let isActive = true;

    const attemptSubscribe = () => {
      if (!isActive) return;

      if (retryCount > maxRetries) {
        console.error(
          `Max retries (${maxRetries}) reached for topic: ${topic}`
        );
        isActive = false;
        if (onError) {
          onError(new Error(`Max retries reached for topic: ${topic}`));
        }
        return;
      }

      try {
        subscription = pubsub.subscribe({ topics: topic }).subscribe({
          next: (msg: any) => {
            const extracted = extractPayload<any>(msg);
            if (!extracted) return;
            const data = this.reassembler.process<T>(extracted);
            if (data) {
              onMessage(data);
            }
          },
          error: (error) => {
            console.error(`Subscription error for topic ${topic}:`, error);
            if (onError) {
              onError(error);
            }

            // Attempt to reconnect
            retryCount++;
            setTimeout(() => {
              attemptSubscribe();
            }, retryDelay);
          },
        });

        // Store the subscription
        if (subscription) {
          this.subscriptions.set(topic, subscription);
        }

        if (onSubscribed) {
          onSubscribed();
        }

        // Reset retry count on successful subscription
        retryCount = 0;
      } catch (err) {
        console.error(`Failed to subscribe to topic ${topic}:`, err);
        if (onError) {
          onError(err);
        }

        // Retry after delay
        retryCount++;
        setTimeout(() => {
          attemptSubscribe();
        }, retryDelay);
      }
    };

    // Start the subscription
    attemptSubscribe();

    // Return subscription control interface
    return {
      unsubscribe: () => {
        isActive = false;
        if (subscription) {
          subscription.unsubscribe();
          this.subscriptions.delete(topic);
        }
      },
      getTopic: () => topic,
      isActive: () => isActive,
    };
  }

  /**
   * Subscribe to project updates
   */
  subscribeToProject(
    accountId: string,
    projectId: string,
    onMessage: (data: any) => void,
    onError?: (error: any) => void
  ): Subscription {
    const topic = `accounts/${accountId}/project/${projectId}`;
    return this.subscribe({
      topic,
      onMessage,
      onError,
    });
  }

  /**
   * Subscribe to project version updates
   */
  subscribeToProjectVersion(
    accountId: string,
    projectVersionId: string,
    onMessage: (data: any) => void,
    onError?: (error: any) => void
  ): Subscription {
    const topic = `accounts/${accountId}/projectVersion/${projectVersionId}`;
    return this.subscribe({
      topic,
      onMessage,
      onError,
    });
  }

  /**
   * Unsubscribe from a specific topic
   */
  unsubscribe(topic: string): void {
    const subscription = this.subscriptions.get(topic);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(topic);
    }
  }

  /**
   * Unsubscribe from all active subscriptions
   */
  unsubscribeAll(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions.clear();
  }

  /**
   * Get all active subscription topics
   */
  getActiveTopics(): string[] {
    return Array.from(this.subscriptions.keys());
  }

  /**
   * Check if subscribed to a specific topic
   */
  isSubscribed(topic: string): boolean {
    return this.subscriptions.has(topic);
  }
}

// Export a singleton instance
export const subscriptionManager = new SubscriptionManager();

// Also export the class for testing or creating custom instances
export { SubscriptionManager };
